import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import * as echarts from 'echarts';
import { Card, Spin, Empty, Button } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

const WaterResourceChart = ({
  data,
  loading = false,
  title = '水资源配置对比分析',
  year,
  region
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [isInitialized, setIsInitialized] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  // 数据有效性检查函数
  const isDataValid = useCallback((chartData) => {
    return chartData &&
           chartData.categories &&
           chartData.categories.length > 0 &&
           chartData.series &&
           chartData.series.length > 0 &&
           chartData.series.every(serie => serie.data && serie.data.length > 0);
  }, []);

  // 初始化或获取图表实例
  const getOrCreateChart = useCallback(() => {
    if (!chartRef.current) {
      console.log('图表容器未就绪');
      return null;
    }

    // 如果实例已存在且未被销毁，直接返回
    if (chartInstance.current && !chartInstance.current.isDisposed()) {
      return chartInstance.current;
    }

    try {
      console.log('创建新的图表实例...');
      
      // 尝试获取已存在的实例
      const existingInstance = echarts.getInstanceByDom(chartRef.current);
      if (existingInstance && !existingInstance.isDisposed()) {
        chartInstance.current = existingInstance;
        console.log('使用已存在的图表实例');
      } else {
        // 创建新实例
        chartInstance.current = echarts.init(chartRef.current, null, {
          renderer: 'canvas',
          useDirtyRect: false
        });
        console.log('创建新的图表实例成功');
      }
      
      setIsInitialized(true);
      return chartInstance.current;
    } catch (error) {
      console.error('图表实例创建失败:', error);
      setIsInitialized(false);
      return null;
    }
  }, []);

  // 销毁图表
  const destroyChart = useCallback(() => {
    if (chartInstance.current && !chartInstance.current.isDisposed()) {
      console.log('销毁图表实例');
      chartInstance.current.dispose();
      chartInstance.current = null;
    }
    setIsInitialized(false);
  }, []);

  // 渲染图表
  const renderChart = useCallback(() => {
    if (!data || !isDataValid(data)) {
      console.log('数据无效，无法渲染图表');
      return false;
    }

    const chart = getOrCreateChart();
    if (!chart) {
      console.log('无法获取图表实例');
      return false;
    }

    console.log('开始渲染图表，数据:', data);

    const option = {
      title: {
        text: title,
        subtext: `${region} - ${year}年`,
        left: 'center',
        textStyle: {
          fontSize: 18,
          fontWeight: 'bold',
          color: '#333'
        },
        subtextStyle: {
          fontSize: 14,
          color: '#666'
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'shadow'
        },
        formatter: function(params) {
          let html = `<div style="font-weight: bold; margin-bottom: 8px;">${params[0].axisValue}</div>`;
          params.forEach(param => {
            html += `
              <div style="margin: 4px 0;">
                <span style="display: inline-block; width: 10px; height: 10px; background-color: ${param.color}; margin-right: 8px;"></span>
                <span style="font-weight: bold;">${param.seriesName}:</span>
                <span style="float: right; margin-left: 20px; font-weight: bold;">${param.value} 亿m³</span>
              </div>
            `;
          });
          
          // 计算差值
          if (params.length === 2) {
            const difference = (params[1].value - params[0].value).toFixed(2);
            const diffColor = difference >= 0 ? '#ff4d4f' : '#52c41a';
            html += `
              <div style="margin: 8px 0; padding-top: 8px; border-top: 1px solid #f0f0f0;">
                <span style="font-weight: bold;">差值:</span>
                <span style="float: right; margin-left: 20px; font-weight: bold; color: ${diffColor};">
                  ${difference >= 0 ? '+' : ''}${difference} 亿m³
                </span>
              </div>
            `;
          }
          
          return html;
        },
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        borderColor: '#ccc',
        borderWidth: 1,
        textStyle: {
          color: '#333'
        }
      },
      legend: {
        top: 50,
        itemGap: 30,
        textStyle: {
          fontSize: 12,
          fontWeight: 'bold'
        }
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '15%',
        top: '20%',
        containLabel: true
      },
      toolbox: {
        feature: {
          saveAsImage: {
            title: '保存为图片',
            name: `water_resource_comparison_${year}_${region.replace(/[^\w]/g, '_')}`
          },
          dataZoom: {
            title: {
              zoom: '区域缩放',
              back: '还原缩放'
            }
          },
          restore: {
            title: '重置'
          },
          magicType: {
            type: ['line', 'bar'],
            title: {
              line: '切换为折线图',
              bar: '切换为柱状图'
            }
          }
        },
        right: 20,
        top: 20
      },
      xAxis: {
        type: 'category',
        data: data.categories,
        axisLabel: {
          rotate: data.categories.length > 10 ? 45 : 0,
          interval: 0,
          fontSize: 11,
          color: '#666',
          formatter: function(value) {
            // 如果标签太长，进行截断
            if (value.length > 8) {
              return value.substring(0, 6) + '...';
            }
            return value;
          }
        },
        axisTick: {
          alignWithLabel: true
        },
        axisLine: {
          lineStyle: {
            color: '#e8e8e8'
          }
        }
      },
      yAxis: {
        type: 'value',
        name: '用水量(亿立方米)',
        nameTextStyle: {
          color: '#666',
          fontSize: 12
        },
        axisLabel: {
          formatter: '{value}',
          color: '#666'
        },
        axisLine: {
          lineStyle: {
            color: '#e8e8e8'
          }
        },
        splitLine: {
          lineStyle: {
            color: '#f5f5f5'
          }
        }
      },
      dataZoom: [
        {
          type: 'slider',
          show: data.categories.length > 12,
          bottom: 50,
          start: 0,
          end: 100,
          height: 20,
          handleStyle: {
            color: '#1890ff'
          },
          textStyle: {
            color: '#666'
          }
        },
        {
          type: 'inside',
          start: 0,
          end: 100
        }
      ],
      series: data.series.map((serie, index) => ({
        name: serie.name,
        type: 'bar',
        data: serie.data,
        itemStyle: {
          color: serie.color,
          borderRadius: [2, 2, 0, 0]
        },
        emphasis: {
          itemStyle: {
            shadowBlur: 10,
            shadowColor: 'rgba(0,0,0,0.2)'
          }
        },
        animationDuration: 1000,
        animationDelay: (idx) => idx * 50,
        label: {
          show: data.categories.length <= 15,
          position: 'top',
          fontSize: 10,
          color: '#666',
          formatter: '{c}'
        }
      }))
    };

    try {
      // 清空之前的配置并设置新配置
      chart.clear();
      chart.setOption(option, true);
      console.log('图表渲染成功');
      return true;
    } catch (error) {
      console.error('设置图表选项失败:', error);
      return false;
    }
  }, [data, title, year, region, isDataValid, getOrCreateChart]);

  // 手动重试渲染
  const handleRetry = useCallback(() => {
    setRetryCount(prev => prev + 1);
    renderChart();
  }, [renderChart]);

  // 组件挂载和数据变化时渲染图表
  useEffect(() => {
    if (!loading && data && isDataValid(data) && chartRef.current) {
      // 使用 requestAnimationFrame 确保 DOM 已更新
      const rafId = requestAnimationFrame(() => {
        const success = renderChart();
        if (!success && retryCount < 3) {
          // 如果渲染失败，稍后重试
          setTimeout(() => {
            setRetryCount(prev => prev + 1);
          }, 500);
        }
      });

      return () => {
        cancelAnimationFrame(rafId);
      };
    }
  }, [loading, data, renderChart, isDataValid, retryCount]);

  // 组件卸载时清理
  useEffect(() => {
    return () => {
      destroyChart();
    };
  }, [destroyChart]);

  // 处理窗口大小变化
  useEffect(() => {
    const handleResize = () => {
      if (chartInstance.current && !chartInstance.current.isDisposed()) {
        chartInstance.current.resize();
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // 如果正在加载，显示加载状态
  if (loading) {
    return (
      <Card>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '500px' 
        }}>
          <Spin size="large" tip="加载数据中..." />
        </div>
      </Card>
    );
  }

  // 如果没有数据或数据无效，显示空状态
  if (!data || !isDataValid(data)) {
    return (
      <Card>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '500px',
          gap: '16px'
        }}>
          <Empty
            description="暂无数据或数据格式错误"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
          />
          {retryCount > 0 && retryCount < 3 && (
            <Button
              icon={<ReloadOutlined />}
              onClick={handleRetry}
            >
              重试加载图表
            </Button>
          )}
        </div>
      </Card>
    );
  }

  return (
    <Card 
      style={{ marginBottom: '24px' }}
      bodyStyle={{ padding: '20px' }}
    >
      <div 
        ref={chartRef} 
        style={{ 
          width: '100%', 
          height: '500px',
          minHeight: '400px'
        }} 
      />
    </Card>
  );
};

export default WaterResourceChart;